import { PocNotificationRole } from 'common/enum';
import { PocNotificationEvents, type PocNotificationRoleField } from 'common/constants';

/*
 * Pure transforms that move a point of contact across the three boundaries where a
 * notification role can silently disappear. Each one is silent by construction in the
 * container, which is why they live here with tests rather than inline.
 *
 *   API data ──normalizePocRoles──▶ form state ──applyPocEdit──▶ form state ──toPocPayload──▶ POST
 *              role ?? flag                      allowlist merge              role !== null
 *              (a contact saved                  (an omitted field            (JSON.stringify
 *               before the roles                  keeps its pre-edit           drops undefined,
 *               shipped)                          value)                       so coalesce)
 *
 * `OrganizationPOCData` is an ambient global, so none of these signatures produce a compile
 * error at a call site that forgets a role. The tests are the guard.
 */

const ROLE_VALUES: string[] = Object.values(PocNotificationRole);

/**
 * The one definition of "this contact receives mail for this event".
 *
 * The role leads and the boolean mirrors it. Deriving the flag through here rather than
 * re-testing `role !== null` at each site means the rule can grow past a bare null check
 * without every caller having to be found and updated in lockstep.
 */
export function isSubscribed(role: Nullable<PocNotificationRoleValue>): boolean {
  return role !== null;
}

/**
 * Give a contact a role for every event it is already subscribed to, and discard any role the
 * enum does not contain.
 *
 * A contact saved before the role columns shipped arrives with `bedCount: true` and
 * `bedCountRole: null` (or absent). Left alone it would open in the modal as unchecked and
 * save back as unsubscribed, quietly cutting off someone who was receiving mail. `NOTIFY` is
 * the value the API's own backfill maps a `true` flag to, so this agrees with the migration.
 *
 * Recognised roles win: this only ever fills a gap, never overwrites a real designation.
 *
 * Unrecognised ones are treated as no role at all. Nothing schema-checks the API response on
 * the way in and `form.reset()` never runs the resolver, so a value outside the enum -- stale
 * data, a casing variant, a role the API ships before the portal does -- would otherwise reach
 * form state unchallenged and then be rejected by the organization-level schema at save time.
 * That failure is not local to the contact: it blocks the entire organization save behind a
 * message that names neither the contact nor the field. Narrowing here is the same membership
 * test `RoleSelect` already applies to values arriving from the control, in the other
 * direction. A contact whose flag is set keeps its subscription as `NOTIFY` rather than being
 * silently unsubscribed on top of losing an unusable designation.
 */
export function normalizePocRoles(poc: OrganizationPOCData): OrganizationPOCData {
  const normalized = { ...poc };

  PocNotificationEvents.forEach(({ flagField, roleField }) => {
    const stored = poc[roleField];
    const recognised = stored != null && ROLE_VALUES.includes(stored);

    normalized[roleField] = recognised
      ? stored
      : poc[flagField]
        ? PocNotificationRole.NOTIFY
        : null;
  });

  return normalized;
}

/**
 * Replace one contact in the list with the edited copy from the modal.
 *
 * Deliberately an allowlist rather than a spread. The modal's form carries view-only keys
 * (`selected`, `visible`) and a `delete` marker the list owns, so a blind spread would let the
 * modal overwrite them. The cost is that a field missing from the list below keeps its
 * pre-edit value and the change appears to save and then reverts -- add both the role and its
 * flag whenever a notification event is added.
 *
 * Returns the original array untouched when `index` addresses no contact, so a stale selection
 * cannot append a phantom row.
 */
export function applyPocEdit(
  pocs: OrganizationPOCData[],
  index: number,
  edited: OrganizationPOCData
): OrganizationPOCData[] {
  const target = pocs[index];
  if (!target) return pocs;

  const updated = [...pocs];
  const next: OrganizationPOCData = {
    ...target,
    firstName: edited.firstName,
    lastName: edited.lastName,
    email: edited.email,
    workPhone: edited.workPhone || null,
    cellPhone: edited.cellPhone || null,
  };

  // Iterated rather than hand-listed. A fifth event added to `PocNotificationEvents` and to
  // `OrganizationPOCData` would compile clean against a hand-written list -- the spread above
  // already satisfies every property the return type needs -- and the new role would silently
  // keep its pre-edit value. That is the exact failure this allowlist exists to make visible,
  // so the list derives from the same constant the rest of the file iterates.
  PocNotificationEvents.forEach(({ flagField, roleField }) => {
    next[roleField] = edited[roleField] ?? null;
    next[flagField] = edited[flagField];
  });

  updated[index] = next;

  return updated;
}

/**
 * Shape one contact for `POST /organization`.
 *
 * The four booleans are derived from the roles rather than read from form state, because they
 * are what the API validates: `PostOrganizationSchema` lists all four in `required` and types
 * them as `boolean`, so a missing or non-boolean value returns 400 for the whole organization
 * save, not just this contact. Deriving also makes clearing a role unsubscribe the contact --
 * reading a stale flag instead would leave them on the list.
 *
 * Every role is coalesced to `null`. `JSON.stringify` omits `undefined` keys entirely, so an
 * undefined role would not reach the API as "no role", it would not reach it at all.
 */
export function toPocPayload(poc: OrganizationPOCData): OrgPOCPayload {
  const resourceRequestRole = poc.resourceRequestRole ?? null;
  const patientTransferRole = poc.patientTransferRole ?? null;
  const inventoryRole = poc.inventoryRole ?? null;
  const bedCountRole = poc.bedCountRole ?? null;

  return {
    id: poc.id || 0, // Ensure id is set to 0 for new POCs
    firstName: poc.firstName,
    lastName: poc.lastName,
    email: poc.email,
    workPhone: poc.workPhone || null,
    cellPhone: poc.cellPhone || null,
    resourceRequestRole,
    patientTransferRole,
    inventoryRole,
    bedCountRole,
    resourceRequest: isSubscribed(resourceRequestRole),
    patientTransfer: isSubscribed(patientTransferRole),
    inventory: isSubscribed(inventoryRole),
    bedCount: isSubscribed(bedCountRole),
    delete: poc.delete || false,
  };
}

/**
 * The PRIMARY and SECONDARY contact for one event, or `null` for a role nobody currently
 * holds. NOTIFY holders are never returned here -- they hold no rank to display.
 */
export interface DesignatedContacts {
  primary: Nullable<OrganizationPOCData>;
  secondary: Nullable<OrganizationPOCData>;
}

/**
 * The one place that answers "who is PRIMARY (or SECONDARY) for this event", replacing the
 * `pocs[0]` / `pocs[1]` position guess that used to answer it at each read-only site
 * independently and could disagree with the others.
 *
 * Positional last-one-wins on a same-role tie, matching `buildPocHolderMap` -- the API is the
 * source of truth for uniqueness, so two contacts holding the same role here is already a data
 * anomaly rather than something this read path needs to resolve.
 */
export function resolveDesignatedContacts(
  pocs: OrganizationPOCData[],
  roleField: PocNotificationRoleField
): DesignatedContacts {
  let primary: Nullable<OrganizationPOCData> = null;
  let secondary: Nullable<OrganizationPOCData> = null;

  pocs.forEach((poc) => {
    if (poc.delete) return;
    if (poc[roleField] === PocNotificationRole.PRIMARY) primary = poc;
    else if (poc[roleField] === PocNotificationRole.SECONDARY) secondary = poc;
  });

  return { primary, secondary };
}

/*
 * THE RULE -- "shares at least one notification event with the recipient"
 *
 *   recipient address ──▶ contacts matching it (0, 1, or several)
 *                               │
 *                     none  ────┴──── one or more
 *                       │                 │
 *                       ▼                 ▼
 *               no scope:          scope = union of their events
 *               show everyone              │
 *                                          ▼
 *                         for each contact:  is it a match on address?  ──yes──▶ show
 *                                                     │ no
 *                                                     ▼
 *                                does it hold no event at all?  ──yes──▶ show
 *                                                     │ no
 *                                                     ▼
 *                        does it share an event with the scope?  ──no──▶ hide
 *                                                     │ yes
 *                                                     ▼
 *                                                   show
 *
 * Intersection, not containment. A contact holding one event the recipient also holds is shown,
 * even when it holds three others the recipient does not.
 *
 * This shipped as containment -- shown only when the recipient held *every* event the contact
 * held -- specified from a pair of contact cards supplied with the request. Reversed on
 * 2026-08-05: run against the real rows behind that facility, the records no longer held the
 * designations the example recorded, so the outcome it asserted could not be reproduced, and the
 * requester's expectation was the looser reading. `pocFormData.test.ts` keeps a case in each
 * direction so the reversal stays legible rather than becoming invisible once the old rule is
 * gone.
 *
 * This is a display convention and nothing enforces it. The magic-link token signs the
 * organization, not the recipient, and the same token goes to every contact on the facility --
 * so the address arrives as query-string text the reader can edit or delete, and deleting it
 * restores the full list by design. Narrowing what a screen draws, not what a caller may read.
 */

/**
 * The recipient an organization magic link names, resolved against this facility's contacts.
 *
 * `address` is the link's value normalised, kept so the predicate can recognise the recipient's
 * own record without taking a second argument. `events` is every notification event held by a
 * contact at that address.
 */
export interface RecipientScope {
  address: string;
  events: ReadonlySet<PocNotificationRoleField>;
}

/*
 * Neither case nor surrounding whitespace is part of an address, and both survive a trip
 * through a mail client and a URL. Coalesced rather than trusted: `OrganizationPOCData` types
 * `email` as `string`, but it is ambient and nothing schema-checks the API response, so one
 * contact with a null address would otherwise throw here and take the whole panel with it --
 * the same guard `cbSearch` applies to the same three fields.
 */
function normalizeAddress(address: Nullable<string> | undefined): string {
  return (address ?? '').trim().toLowerCase();
}

/**
 * The notification events the link's recipient holds, or `null` when the link names nobody.
 *
 * `null` is the unscoped answer and covers three cases that need the same treatment: no address
 * on the link at all (the admin-copied URL, or a stripped parameter), an address that is present
 * but empty, and an address no contact on this facility holds (a typo, a forwarded link, or a
 * contact removed after the mail went out). Each one falls open to the full list.
 *
 * Every contact at the address contributes, rather than the first one found: the API indexes
 * this column non-uniquely, so duplicates within a facility are possible and a positional pick
 * would silently depend on row order.
 *
 * Reads the whole list, including contacts marked for deletion. Removing a card -- including
 * the recipient's own -- narrows what the panel draws; it must not quietly unresolve the scope
 * and widen the list back out mid-visit.
 */
export function resolveRecipientScope(
  pocs: OrganizationPOCData[],
  address: Nullable<string> | undefined
): Nullable<RecipientScope> {
  const recipient = normalizeAddress(address);
  // Short-circuits before any contact is compared, so a contact whose own address is null or
  // empty can never resolve as the recipient by matching an empty string against another.
  if (!recipient) return null;

  const events = new Set<PocNotificationRoleField>();
  let resolved = false;

  pocs.forEach((poc) => {
    if (normalizeAddress(poc.email) !== recipient) return;
    resolved = true;
    PocNotificationEvents.forEach(({ roleField }) => {
      if (isSubscribed(poc[roleField] ?? null)) events.add(roleField);
    });
  });

  return resolved ? { address: recipient, events } : null;
}

/**
 * Does this contact share a notification event with the recipient?
 *
 *   happy: a contact holding any event the recipient also holds is shown
 *   nil/null: no scope resolved matches every contact, which is the unfiltered list
 *   empty/zero: a contact holding no events at all is always shown, through an explicit guard --
 *     it has nothing to share, so the overlap test alone would hide it from everyone. A contact
 *     added during the visit starts here
 *   upstream error: a role field that is absent rather than null reads as unsubscribed, via the
 *     `?? null`. `isSubscribed` tests `!== null`, so a bare `undefined` would otherwise count as
 *     an event held. The direction this errs in flipped with the rule and is worth stating: under
 *     containment a missing key showed a contact to *more* recipients, under intersection it shows
 *     them to fewer, because a smaller held set has fewer chances to overlap. A contact whose keys
 *     are all absent lands on the holds-nothing guard and is still shown to everyone
 *
 * The recipient's own record is exempt and always shown -- every record at the address, since
 * the scope was unioned across all of them.
 *
 * That exemption fires for no scope this module can currently produce: `resolveRecipientScope`
 * unions the events of every record at the address, so a record at that address always shares one
 * with the union and the overlap test below already passes for it -- and a recipient holding
 * nothing at all reaches the holds-nothing guard first. It is kept because Requirement 2 is a
 * requirement rather than a corollary -- "the recipient always sees themself" should hold because
 * the address matched, not because of how the scope happened to be assembled. Narrowing the
 * resolver to an intersection across duplicates, say, would break it silently otherwise.
 * `pocFormData.test.ts` exercises the branch against a hand-built scope.
 */
export function isWithinRecipientScope(
  poc: OrganizationPOCData,
  scope: Nullable<RecipientScope>
): boolean {
  if (!scope) return true;
  /*
   * A contact added during this visit and not yet saved is always shown to whoever added it.
   * `id: 0` is `usePocModalForm`'s default and the marker the container already reads as
   * "unsaved" (`OrgDetails.container.tsx`, the `buildPocHolderMap` exclusion comment).
   *
   * The add flow can designate: `PocModal`'s notification table is not gated on `isUpdate`, so a
   * new contact can be created holding an event the recipient lacks and would otherwise be
   * scoped out at the moment it was created -- leaving the person who just added it with no
   * evidence it exists, and inviting them to add it again. `onAddPoc` clears the notification
   * filter to avoid exactly that, and this is the scope's version of the same move: the scope is
   * not clearable, so the exemption has to live here.
   */
  if (poc.id === 0) return true;
  if (normalizeAddress(poc.email) === scope.address) return true;

  const held = PocNotificationEvents.filter(({ roleField }) =>
    isSubscribed(poc[roleField] ?? null)
  );

  /*
   * Requirement 3, and the reason this is not a bare `.some()`: a contact designated for nothing
   * shares nothing, so the overlap test below would hide it from every recipient. Under the
   * containment rule this case fell out of `.every()` for free -- a contact holding nothing could
   * not hold something the recipient lacked. Intersection has to state it.
   */
  if (held.length === 0) return true;

  return held.some(({ roleField }) => scope.events.has(roleField));
}

export interface PocRoleHolder {
  fullName: string;
}

/**
 * For each event, who holds PRIMARY and who holds SECONDARY. NOTIFY has no bucket here --
 * unlike the other two roles any number of contacts can hold it, so there is never anyone to
 * displace and never anyone to name in a warning.
 */
export type PocHolderMap = Record<
  PocNotificationRoleField,
  { PRIMARY: Nullable<PocRoleHolder>; SECONDARY: Nullable<PocRoleHolder> }
>;

/**
 * Who currently holds PRIMARY and SECONDARY for each event, across every contact except the one
 * at `excludeIndex` -- the contact this map is being built for, so a contact re-selecting a role
 * it already holds never reads back as displacing itself. `excludeIndex` is `null` for a
 * contact that has not been added to `pocs` yet, the new-contact case, where nothing needs
 * excluding.
 *
 * Positional, not by `poc.id`: a contact added this session and not yet saved carries `id: 0`
 * (`usePocModalForm`'s default), and nothing assigns it a real id until the API round-trips it.
 * Two such contacts added in the same session before a save would share `id: 0` and hide each
 * other from an id-based exclusion.
 *
 * Built from whatever `pocs` list the caller passes, not a fetch, so a contact added or
 * reassigned earlier in this session -- neither saved to the API yet -- is already reflected.
 * A contact marked for deletion is skipped: after save it will not hold anything to take.
 *
 * `scope` narrows the *names* to the contacts a scoped recipient can already see, not the
 * warnings. A holder outside the scope still occupies its role and is still announced, under
 * `UNNAMED_HOLDER` rather than its own name -- the panel hides that contact, so naming it here
 * would put it back on screen one surface over. Dropping the holder entirely was the other
 * option and is worse: the recipient would take the role with no warning at all and the real
 * holder would lose it silently on save, which is exactly the invisible failure this map exists
 * to prevent. `null` scope -- the authenticated route and every unscoped link -- names everyone,
 * as before.
 */
export const UNNAMED_HOLDER = 'Another contact';

export function buildPocHolderMap(
  pocs: OrganizationPOCData[],
  excludeIndex: number | null,
  scope: Nullable<RecipientScope> = null
): PocHolderMap {
  const map = PocNotificationEvents.reduce((acc, { roleField }) => {
    acc[roleField] = { PRIMARY: null, SECONDARY: null };
    return acc;
  }, {} as PocHolderMap);

  pocs.forEach((poc, index) => {
    if (index === excludeIndex || poc.delete) return;

    // `|| ''` per field, matching OrgInfo.tsx's precedent: `OrganizationPOCData` is ambient and
    // nothing schema-checks the API response inbound, so an unguarded null/undefined field would
    // interpolate as the literal "null"/"undefined" rather than an empty string.
    const realName = `${poc.firstName || ''} ${poc.lastName || ''}`.trim();
    // A holder with no name would render a warning naming nobody -- worse than no warning.
    if (!realName) return;
    const fullName = isWithinRecipientScope(poc, scope) ? realName : UNNAMED_HOLDER;

    PocNotificationEvents.forEach(({ roleField }) => {
      const role = poc[roleField];
      if (role === PocNotificationRole.PRIMARY || role === PocNotificationRole.SECONDARY) {
        map[roleField][role] = { fullName };
      }
    });
  });

  return map;
}
